import * as THREE from 'three'

/**
 * Procedural canvas textures + the material palette for the mall interior.
 *
 * The palette is drawn from how real shopping centres are actually finished:
 * a warm terrazzo/stone concourse floor, limestone-clad tenant piers, off-white
 * plaster soffits, bronze mullions, and daylight through a glazed roof. Nothing
 * here is loaded over the network — every surface is drawn to a canvas at
 * runtime, so the mall renders instantly and offline.
 */
/*
 * Albedos are deliberately mid-tone, not the near-white you'd guess from mall
 * photography. Photographs of malls look bright because the *lighting* is
 * bright; if you also make every surface near-white, the whole frame clips and
 * the architecture disappears. The tonal hierarchy here runs, brightest first:
 * shopfront lightbox → vault glazing → soffit → pier → floor → granite inlay.
 */
export const MALL = {
  // Floor
  concourseStone: '#cdc4b3',
  bandStone: '#9b8e78',
  granite: '#3b3934',
  // Vertical surfaces
  limestone: '#dcd4c6',
  plaster: '#e8e2d7',
  bronze: '#4f463c',
  bronzeLight: '#8a7a63',
  // Glazing / light
  daylight: '#f2f8ff',
  skyTint: '#bcd6ef',
  glassTint: '#8fadbd',
  warmLight: '#ffd8a4',
  // Fixtures
  foliage: '#4d7d50',
  foliageDeep: '#31583a',
  wood: '#8c6a46',
  soil: '#463729',
  ink: '#2b2a28',
} as const

// ── Color helpers ──────────────────────────────────────────────────────

/**
 * The app's category colors are tuned for a near-black backdrop, so they read
 * washed-out when printed on a limestone fascia. This deepens them into a
 * signage-weight tone that holds up against light stone, the way real vinyl and
 * channel-letter signage does.
 */
export function signageColor(hex: string): string {
  const c = new THREE.Color(hex)
  const hsl = { h: 0, s: 0, l: 0 }
  c.getHSL(hsl)
  c.setHSL(hsl.h, Math.min(1, hsl.s * 1.15), Math.min(hsl.l, 0.42))
  return `#${c.getHexString()}`
}

function rgba(hex: string, alpha: number): string {
  const c = new THREE.Color(hex)
  return `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${alpha})`
}

/** Deterministic PRNG so a given floor always looks identical between sessions. */
function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function canvas2d(w: number, h: number) {
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  return { canvas: c, ctx: c.getContext('2d')! }
}

function toTexture(canvas: HTMLCanvasElement, repeatX = 1, repeatY = 1): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.repeat.set(repeatX, repeatY)
  tex.anisotropy = 8
  return tex
}

export function roundedRectPath(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number
) {
  const rad = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rad, y)
  ctx.arcTo(x + w, y, x + w, y + h, rad)
  ctx.arcTo(x + w, y + h, x, y + h, rad)
  ctx.arcTo(x, y + h, x, y, rad)
  ctx.arcTo(x, y, x + w, y, rad)
  ctx.closePath()
}

/** Fits text to a max width by stepping the font size down. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, startPx: number, weight: string, family = 'system-ui, sans-serif') {
  let px = startPx
  do {
    ctx.font = `${weight} ${px}px ${family}`
    if (ctx.measureText(text).width <= maxWidth) break
    px -= 2
  } while (px > 10)
  return px
}

// ── Floor: terrazzo ────────────────────────────────────────────────────

/**
 * Epoxy terrazzo — the default hard-wearing concourse floor in real centres:
 * marble/glass chips suspended in a warm binder, ground and polished flat.
 * Chips near a tile edge are redrawn on the opposite side so the texture tiles
 * seamlessly across a 100 m concourse.
 */
export function makeTerrazzoTexture(seed = 7, repeat = 12): THREE.CanvasTexture {
  const S = 512
  const { canvas, ctx } = canvas2d(S, S)
  const rand = mulberry32(seed)

  ctx.fillStyle = MALL.concourseStone
  ctx.fillRect(0, 0, S, S)

  // Faint cloudiness in the binder so large areas don't read as flat color
  for (let i = 0; i < 26; i++) {
    const g = ctx.createRadialGradient(rand() * S, rand() * S, 0, rand() * S, rand() * S, 90 + rand() * 120)
    g.addColorStop(0, rgba(rand() > 0.5 ? '#ffffff' : '#8d8271', 0.07))
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, S, S)
  }

  const chipTones = ['#f3ecdf', '#8d8070', '#5c5448', '#b98d5f', '#7d8a84', '#332f2a']
  const drawChip = (x: number, y: number, rx: number, ry: number, rot: number, fill: string, alpha: number) => {
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.fillStyle = fill
    ctx.beginPath()
    ctx.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  // Real terrazzo is dense — lots of small chips, not a scatter of pebbles
  for (let i = 0; i < 5200; i++) {
    const x = rand() * S
    const y = rand() * S
    const rx = 0.9 + rand() * 2.6
    const ry = rx * (0.55 + rand() * 0.5)
    const rot = rand() * Math.PI
    const fill = chipTones[(rand() * chipTones.length) | 0]
    const alpha = 0.3 + rand() * 0.45
    // Wrap chips across the seam so the tile repeats invisibly
    const xs = x < 6 ? [x, x + S] : x > S - 6 ? [x, x - S] : [x]
    const ys = y < 6 ? [y, y + S] : y > S - 6 ? [y, y - S] : [y]
    for (const cx of xs) for (const cy of ys) drawChip(cx, cy, rx, ry, rot, fill, alpha)
  }

  // Sawn joints on a 2×2 grid within the tile. Without a joint line there is
  // nothing to read scale against and the floor turns into visual soup.
  ctx.strokeStyle = rgba('#6f6556', 0.28)
  ctx.lineWidth = 2
  for (const p of [0, S / 2]) {
    ctx.beginPath(); ctx.moveTo(p + 1, 0); ctx.lineTo(p + 1, S); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(0, p + 1); ctx.lineTo(S, p + 1); ctx.stroke()
  }

  return toTexture(canvas, repeat, repeat)
}

/** Honed stone border band that frames the concourse along the shopfronts. */
export function makeBandStoneTexture(): THREE.CanvasTexture {
  const S = 256
  const { canvas, ctx } = canvas2d(S, S)
  const rand = mulberry32(21)

  ctx.fillStyle = MALL.bandStone
  ctx.fillRect(0, 0, S, S)
  for (let i = 0; i < 700; i++) {
    ctx.globalAlpha = 0.05 + rand() * 0.12
    ctx.fillStyle = rand() > 0.5 ? '#ffffff' : '#5f5546'
    ctx.fillRect(rand() * S, rand() * S, 1 + rand() * 3, 1 + rand() * 2)
  }
  ctx.globalAlpha = 1
  // Sawn joints, 1 slab per tile
  ctx.strokeStyle = rgba('#6b6152', 0.55)
  ctx.lineWidth = 2
  ctx.strokeRect(1, 1, S - 2, S - 2)

  return toTexture(canvas, 1, 1)
}

/** Cast limestone cladding for tenant piers and the fascia band. */
export function makeLimestoneTexture(): THREE.CanvasTexture {
  const S = 256
  const { canvas, ctx } = canvas2d(S, S)
  const rand = mulberry32(41)

  ctx.fillStyle = MALL.limestone
  ctx.fillRect(0, 0, S, S)
  // Kept very low-contrast: any stronger and the mottling reads as dirt on the wall
  for (let i = 0; i < 26; i++) {
    const g = ctx.createRadialGradient(rand() * S, rand() * S, 0, rand() * S, rand() * S, 24 + rand() * 40)
    g.addColorStop(0, rgba(rand() > 0.5 ? '#ffffff' : '#bcb2a0', 0.055))
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, S, S)
  }
  for (let i = 0; i < 900; i++) {
    ctx.globalAlpha = 0.03 + rand() * 0.06
    ctx.fillStyle = rand() > 0.5 ? '#ffffff' : '#9d9382'
    ctx.fillRect(rand() * S, rand() * S, 1, 1 + rand())
  }
  return toTexture(canvas, 1, 1)
}

// ── Ceiling: glazed roof ───────────────────────────────────────────────

/**
 * The inside face of the barrel-vault skylight, painted rather than lit so the
 * roof reads as a light source without costing a real light.
 *
 * The tile maps to one structural bay: image X runs around the arc (brightest
 * at the ridge in the middle), image Y runs along the concourse, so repeating
 * it along Y gives one glazing bar per bay. `repeatY` should be the bay count.
 */
export function makeSkylightTexture(repeatY: number): THREE.CanvasTexture {
  const W = 256
  const H = 128
  const { canvas, ctx } = canvas2d(W, H)

  // Across the arc: sky at the springing, blown-out white at the ridge
  const g = ctx.createLinearGradient(0, 0, W, 0)
  g.addColorStop(0, MALL.skyTint)
  g.addColorStop(0.4, MALL.daylight)
  g.addColorStop(0.5, '#ffffff')
  g.addColorStop(0.6, MALL.daylight)
  g.addColorStop(1, MALL.skyTint)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  // Longitudinal purlins — run the length of the vault
  ctx.fillStyle = rgba(MALL.bronze, 0.34)
  for (const u of [0.2, 0.5, 0.8]) ctx.fillRect(u * W - 2, 0, 4, H)

  // One transverse glazing bar per bay, at the tile seam
  ctx.fillStyle = rgba(MALL.bronze, 0.5)
  ctx.fillRect(0, 0, W, 5)

  return toTexture(canvas, 1, Math.max(1, Math.round(repeatY)))
}

/**
 * Full-height glazed end wall. Real concourses terminate in daylight rather
 * than a blank wall — it gives the corridor a destination to walk toward.
 */
export function makeCurtainWallTexture(): THREE.CanvasTexture {
  const W = 512
  const H = 512
  const { canvas, ctx } = canvas2d(W, H)

  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, '#bcd8f2')
  g.addColorStop(0.45, '#e6f1fb')
  g.addColorStop(0.72, '#f6f6f0')
  g.addColorStop(1, '#d8d3c6')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  // Blurred exterior massing along the horizon, well out of focus
  ctx.globalAlpha = 0.22
  ctx.fillStyle = '#7f9bb4'
  for (let i = 0; i < 9; i++) {
    const bw = 30 + ((i * 53) % 70)
    const bh = 40 + ((i * 91) % 110)
    ctx.fillRect((i * 61) % W, H * 0.7 - bh, bw, bh)
  }
  ctx.globalAlpha = 1

  // Mullion grid — kept light, or the end wall reads as a cage rather than a window
  ctx.fillStyle = rgba(MALL.bronze, 0.5)
  for (let i = 0; i <= 4; i++) ctx.fillRect((i / 4) * W - 2.5, 0, 5, H)
  for (let j = 0; j <= 4; j++) ctx.fillRect(0, (j / 4) * H - 2.5, W, 5)

  return toTexture(canvas, 1, 1)
}

/** Perforated metal ceiling panel used on the flat soffits either side of the vault. */
export function makeSoffitTexture(): THREE.CanvasTexture {
  const S = 128
  const { canvas, ctx } = canvas2d(S, S)
  ctx.fillStyle = MALL.plaster
  ctx.fillRect(0, 0, S, S)
  // Panel joints on a 1-tile grid
  ctx.strokeStyle = rgba('#cfc7b9', 0.9)
  ctx.lineWidth = 2
  ctx.strokeRect(1, 1, S - 2, S - 2)
  // Fine perforation
  ctx.fillStyle = rgba('#c3bbad', 0.5)
  for (let y = 8; y < S; y += 12) {
    for (let x = 8; x < S; x += 12) {
      ctx.beginPath()
      ctx.arc(x, y, 1.6, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  return toTexture(canvas, 1, 1)
}

// ── Storefront ─────────────────────────────────────────────────────────

/**
 * What you see through a tenant's glazing before you walk in: a warm, softly-lit
 * interior with a lit ceiling slot and merchandise racks in silhouette. Sold as
 * a painted backdrop plane a couple of metres behind the glass, which is enough
 * to give the shopfront real depth at walking speed.
 */
export function makeShopInteriorTexture(accent: string): THREE.CanvasTexture {
  const W = 512
  const H = 384
  const { canvas, ctx } = canvas2d(W, H)

  // Kept a stop darker than the concourse so the bays read as recessed volumes
  // rather than as more wall.
  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, '#d9cfbe')
  g.addColorStop(0.22, '#c2b6a2')
  g.addColorStop(0.75, '#a1937f')
  g.addColorStop(1, '#847764')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  // Lit ceiling slots running back into the shop
  ctx.fillStyle = rgba('#fff8e8', 0.9)
  ctx.fillRect(W * 0.12, H * 0.06, W * 0.76, 9)
  ctx.globalAlpha = 0.5
  ctx.fillRect(W * 0.2, H * 0.13, W * 0.6, 5)
  ctx.globalAlpha = 1

  // Back wall accent panel — the tenant's brand color, seen deep inside
  ctx.fillStyle = rgba(signageColor(accent), 0.45)
  ctx.fillRect(W * 0.3, H * 0.2, W * 0.4, H * 0.42)

  // Racks / gondolas in silhouette
  ctx.fillStyle = rgba('#3a332b', 0.45)
  for (const [x, w, h] of [[0.06, 0.1, 0.36], [0.82, 0.12, 0.34], [0.24, 0.07, 0.24], [0.68, 0.07, 0.24]] as const) {
    ctx.fillRect(W * x, H * (0.92 - h), W * w, H * h)
  }
  // Floor reflection under them
  ctx.fillStyle = rgba('#ffffff', 0.12)
  ctx.fillRect(0, H * 0.9, W, H * 0.1)

  return toTexture(canvas, 1, 1)
}

/**
 * Illuminated fascia sign — the tenant's name in channel letters on the
 * bulkhead above the shopfront. Transparent background so it mounts onto the
 * limestone band rather than covering it, matching how mall signage criteria
 * require signage to sit proud of the neutral pier line.
 */
export function makeFasciaSignTexture(name: string, accent: string): THREE.CanvasTexture {
  const W = 1024
  const H = 190
  const { canvas, ctx } = canvas2d(W, H)
  const deep = signageColor(accent)

  const label = name.length > 22 ? `${name.slice(0, 21)}…` : name
  const text = label.toUpperCase()

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const px = fitText(ctx, text, W * 0.82, 132, '800')
  ctx.letterSpacing = `${Math.round(px * 0.05)}px`
  ctx.font = `800 ${px}px system-ui, sans-serif`

  // Halo-lit letters: warm spill on the stone behind, then the face
  ctx.save()
  ctx.shadowColor = rgba(accent, 0.85)
  ctx.shadowBlur = 34
  ctx.fillStyle = deep
  ctx.fillText(text, W / 2, H / 2)
  ctx.fillText(text, W / 2, H / 2)
  ctx.restore()

  ctx.fillStyle = deep
  ctx.fillText(text, W / 2, H / 2)

  // Lit edge along the top of each letter
  ctx.strokeStyle = rgba('#ffffff', 0.5)
  ctx.lineWidth = 1.5
  ctx.strokeText(text, W / 2, H / 2 - 1)

  // Brand mark: a filled disc at the sign's left, like a mounted logo plate
  const markX = W / 2 - ctx.measureText(text).width / 2 - 46
  if (markX > 26) {
    ctx.beginPath()
    ctx.arc(markX, H / 2, 17, 0, Math.PI * 2)
    ctx.fillStyle = accent
    ctx.shadowColor = rgba(accent, 0.9)
    ctx.shadowBlur = 20
    ctx.fill()
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

/**
 * Blade sign hung over the concourse at the entrance to each category wing —
 * the wayfinding gateway you read before you commit to walking down it.
 */
export function makeGatewaySignTexture(category: string, accent: string, shopCount: number): THREE.CanvasTexture {
  const W = 1024
  const H = 256
  const { canvas, ctx } = canvas2d(W, H)

  // Dark plate, light type — the standard for overhead directional signage,
  // and the only thing on screen that isn't washed in daylight.
  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, '#413a33')
  g.addColorStop(0.5, '#332e28')
  g.addColorStop(1, '#26221d')
  ctx.fillStyle = g
  roundedRectPath(ctx, 6, 6, W - 12, H - 12, 10)
  ctx.fill()

  // Accent rule down the leading edge — the wing's wayfinding color
  ctx.fillStyle = accent
  ctx.fillRect(16, 16, 14, H - 32)

  ctx.strokeStyle = rgba('#8a7a63', 0.5)
  ctx.lineWidth = 3
  roundedRectPath(ctx, 6, 6, W - 12, H - 12, 10)
  ctx.stroke()

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  const title = category.toUpperCase()
  const px = fitText(ctx, title, W - 160, 100, '800')
  ctx.letterSpacing = '3px'
  ctx.font = `800 ${px}px system-ui, sans-serif`
  ctx.fillStyle = '#f7f3ec'
  ctx.fillText(title, 62, H * 0.6)

  ctx.letterSpacing = '5px'
  ctx.font = '700 28px system-ui, sans-serif'
  ctx.fillStyle = rgba(accent, 0.95)
  ctx.fillText(`${shopCount} STORE${shopCount === 1 ? '' : 'S'}`, 64, H * 0.85)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

/**
 * Wing-identity graphic for the blank stone returns at the concourse mouth.
 * Centres treat that wall as a feature panel rather than leaving it flat, and
 * without something there the entrance apron reads as an unfinished box.
 */
export function makeWingGraphicTexture(category: string, accent: string): THREE.CanvasTexture {
  const W = 1024
  const H = 512
  const { canvas, ctx } = canvas2d(W, H)

  // Honed feature stone, a shade deeper than the surrounding wall
  const g = ctx.createLinearGradient(0, 0, W * 0.4, H)
  g.addColorStop(0, '#b7ab98')
  g.addColorStop(0.55, '#a89b87')
  g.addColorStop(1, '#8e8270')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  const rand = mulberry32(97)
  for (let i = 0; i < 1200; i++) {
    ctx.globalAlpha = 0.03 + rand() * 0.07
    ctx.fillStyle = rand() > 0.5 ? '#ffffff' : '#6b6152'
    ctx.fillRect(rand() * W, rand() * H, 1 + rand() * 3, 1)
  }
  ctx.globalAlpha = 1

  // Vertical stone joints
  ctx.strokeStyle = rgba('#6b6152', 0.35)
  ctx.lineWidth = 2
  for (let i = 1; i < 4; i++) {
    ctx.beginPath(); ctx.moveTo((i / 4) * W, 0); ctx.lineTo((i / 4) * W, H); ctx.stroke()
  }

  // Tone-on-tone wing name, cut into the stone rather than printed on it
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const title = category.toUpperCase()
  // Set small and centred: the wall is only ever seen at an oblique angle, and
  // oversized type there just reads as a word cropped in half.
  const px = fitText(ctx, title, W * 0.42, 92, '800')
  ctx.letterSpacing = '10px'
  ctx.font = `800 ${px}px system-ui, sans-serif`
  ctx.fillStyle = rgba('#ffffff', 0.28)
  ctx.fillText(title, W / 2, H * 0.46 - 3)
  ctx.fillStyle = rgba('#4a4238', 0.34)
  ctx.fillText(title, W / 2, H * 0.46)
  ctx.letterSpacing = '0px'

  // Accent rule beneath
  ctx.fillStyle = accent
  ctx.fillRect(W * 0.34, H * 0.66, W * 0.32, 7)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

/** Backlit directory pylon standing at the mouth of the wing. */
export function makeDirectoryTexture(category: string, accent: string, names: string[]): THREE.CanvasTexture {
  const W = 512
  const H = 1024
  const { canvas, ctx } = canvas2d(W, H)
  const deep = signageColor(accent)

  ctx.fillStyle = '#f7f4ee'
  ctx.fillRect(0, 0, W, H)

  ctx.fillStyle = deep
  ctx.fillRect(0, 0, W, 132)
  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  const px = fitText(ctx, category.toUpperCase(), W - 84, 60, '800')
  ctx.letterSpacing = '2px'
  ctx.font = `800 ${px}px system-ui, sans-serif`
  ctx.fillText(category.toUpperCase(), 42, 68)
  ctx.letterSpacing = '0px'

  ctx.font = '700 30px system-ui, sans-serif'
  names.slice(0, 16).forEach((name, i) => {
    const y = 190 + i * 50
    ctx.fillStyle = rgba(MALL.ink, 0.82)
    const label = name.length > 20 ? `${name.slice(0, 19)}…` : name
    ctx.fillText(label, 42, y)
    ctx.fillStyle = rgba(MALL.ink, 0.12)
    ctx.fillRect(42, y + 22, W - 84, 1)
  })

  ctx.fillStyle = rgba(MALL.ink, 0.35)
  ctx.font = '700 24px system-ui, sans-serif'
  ctx.letterSpacing = '4px'
  ctx.fillText('DIRECTORY', 42, H - 46)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

/** Shown in a tenant's lightbox until its real poster arrives. */
export function makePosterPlaceholderTexture(accent: string): THREE.CanvasTexture {
  const W = 512
  const H = 320
  const { canvas, ctx } = canvas2d(W, H)
  const deep = signageColor(accent)

  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, '#ffffff')
  g.addColorStop(1, '#ece5da')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  ctx.fillStyle = rgba(accent, 0.1)
  ctx.fillRect(0, H * 0.62, W, H * 0.38)

  // Awning-and-window shopfront glyph, drawn as strokes (no emoji, per the app's icon convention)
  ctx.strokeStyle = deep
  ctx.lineWidth = 7
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  const cx = W / 2
  ctx.beginPath()
  ctx.moveTo(cx - 82, 214); ctx.lineTo(cx - 82, 150); ctx.lineTo(cx - 60, 104)
  ctx.lineTo(cx + 60, 104); ctx.lineTo(cx + 82, 150); ctx.lineTo(cx + 82, 214)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(cx - 96, 150); ctx.lineTo(cx + 96, 150)
  ctx.stroke()
  ctx.strokeRect(cx - 30, 166, 60, 48)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// ── Light + reflection decals ──────────────────────────────────────────

/** Radial falloff used for downlight pools on the floor. */
export function makeLightPoolTexture(): THREE.CanvasTexture {
  const S = 256
  const { canvas, ctx } = canvas2d(S, S)
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2)
  g.addColorStop(0, 'rgba(255,255,255,0.95)')
  g.addColorStop(0.45, 'rgba(255,255,255,0.34)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, S, S)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/**
 * Vertical smear used to fake a shopfront's reflection in the polished floor,
 * and the light wash spilling down the fascia. Cheap stand-in for a real
 * reflection pass, and at eye height it reads the same.
 */
export function makeVerticalFalloffTexture(): THREE.CanvasTexture {
  const W = 8
  const H = 256
  const { canvas, ctx } = canvas2d(W, H)
  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, 'rgba(255,255,255,0.85)')
  g.addColorStop(0.35, 'rgba(255,255,255,0.28)')
  g.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/** Soft contact shadow dropped under piers, planters and benches. */
export function makeContactShadowTexture(): THREE.CanvasTexture {
  const S = 128
  const { canvas, ctx } = canvas2d(S, S)
  const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2)
  g.addColorStop(0, 'rgba(0,0,0,0.5)')
  g.addColorStop(0.55, 'rgba(0,0,0,0.18)')
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, S, S)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}
